export async function useFetchCountySubdivision({ latitude, longitude }) {
  if (!isNumber(latitude) || !isNumber(longitude)) {
    throw new Error("Invalid coordinates");
  }
  const params = computed(() => ({
    latitude: round(latitude, 4),
    longitude: round(longitude, 4),
  }));
  return useLazyFetch("/api/county-subdivison", {
    params,
  });
}

export async function useFetchZone({ latitude, longitude }) {
  if (!isNumber(latitude) || !isNumber(longitude)) {
    throw new Error("Invalid coordinates");
  }
  const params = computed(() => ({
    latitude: round(latitude, 4),
    longitude: round(longitude, 4),
  }));
  return useLazyFetch("/api/zone", {
    params,
  });
}

export async function useFetchSchoolDistrict({ latitude, longitude }) {
  if (!isNumber(latitude) || !isNumber(longitude)) {
    throw new Error("Invalid coordinates");
  }
  const params = computed(() => ({
    latitude: round(latitude, 4),
    longitude: round(longitude, 4),
  }));
  return useLazyFetch("/api/school-district", {
    params,
  });
}

export async function useFetchCicadas({ latitude, longitude }) {
  if (!isNumber(latitude) || !isNumber(longitude)) {
    throw new Error("Invalid coordinates");
  }
  const params = computed(() => ({
    latitude: round(latitude, 4),
    longitude: round(longitude, 4),
  }));
  return useLazyFetch("/api/cicadas", {
    params,
  });
}

export async function useFetchSchoolDistrictDetails({ id }) {
  if (!id) {
    throw new Error("Invalid id");
  }
  return useLazyFetch("/api/school-district/details", {
    params: { id },
  });
}

export async function useFetchPeaks({ latitude, longitude }) {
  if (!isNumber(latitude) || !isNumber(longitude)) {
    throw new Error("Invalid coordinates");
  }
  const params = computed(() => ({
    latitude: round(latitude, 4),
    longitude: round(longitude, 4),
  }));
  return useLazyFetch("/api/peaks", {
    params,
  });
}

export async function useFetchSnow({ latitude, longitude }, date = null) {
  if (!isNumber(latitude) || !isNumber(longitude)) {
    throw new Error("Invalid coordinates");
  }
  const params = computed(() => ({
    latitude: round(latitude, 4),
    longitude: round(longitude, 4),
    date: date ?? undefined,
  }));
  return useLazyFetch("/api/snow", {
    params,
  });
}

export async function useFetchSnowExtremes({ latitude, longitude }) {
  if (!isNumber(latitude) || !isNumber(longitude)) {
    throw new Error("Invalid coordinates");
  }
  const params = computed(() => ({
    latitude: round(latitude, 4),
    longitude: round(longitude, 4),
  }));
  return useLazyFetch("/api/snow/extremes", {
    params,
  });
}

export async function useFetchForecast({ latitude, longitude }, type) {
  if (!isNumber(latitude) || !isNumber(longitude)) {
    throw new Error("Invalid coordinates");
  }
  const params = computed(() => ({
    latitude: round(latitude, 2),
    longitude: round(longitude, 2),
    type,
  }));
  return useLazyFetch("/api/forecast", {
    params,
  });
}

export async function useFetchElevation({ latitude, longitude }) {
  if (!isNumber(latitude) || !isNumber(longitude)) {
    throw new Error("Invalid coordinates");
  }
  const params = computed(() => ({
    latitude: round(latitude, 5),
    longitude: round(longitude, 5),
  }));
  return useLazyFetch("/api/elevation", {
    params,
    transform: (response) => response?.elevation,
  });
}

// Outcome of a finished elevation lookup: "ok", "none" (the API returned a null elevation because there is
// no data for the point, e.g. open ocean) or "failed". 0 m is a valid elevation.
export function elevationLookupStatus(elevation, error) {
  if (isNumber(elevation)) return "ok";
  if (error) return "failed";
  return "none";
}

// Elevation data sources covering a location, finest resolution first. Refetches when location changes,
// and skips fetching while the location is empty.
export function useFetchElevationCoverage(location) {
  const params = computed(() => {
    const { latitude, longitude } = toValue(location) || {};
    if (!isNumber(latitude) || !isNumber(longitude)) return null;
    return { latitude: round(latitude, 5), longitude: round(longitude, 5) };
  });
  return useLazyAsyncData(
    () =>
      params.value ? `elevation-coverage-${params.value.latitude},${params.value.longitude}` : "elevation-coverage",
    async () => {
      if (!params.value) return [];
      const response = await $fetch("/api/elevation/coverage", { params: params.value });
      return response?.attributions || [];
    },
  );
}

// Plain $fetch versions, for loading inside useAsyncData (e.g. a page's initial data, rendered on the server), where
// useFetch can't be used. Same rounding as the useFetch* helpers above.
export function fetchCountySubdivision({ latitude, longitude }) {
  return $fetch("/api/county-subdivison", { params: { latitude: round(latitude, 4), longitude: round(longitude, 4) } });
}

export function fetchZone({ latitude, longitude }) {
  return $fetch("/api/zone", { params: { latitude: round(latitude, 4), longitude: round(longitude, 4) } });
}
